import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, InputGroup
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const CallToActionManager = () => {
  const [ctas, setCtas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingCta, setEditingCta] = useState(null);
  
  // Options
  const [availableColors, setAvailableColors] = useState({});
  const [buttonVariants, setButtonVariants] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    buttons: [],
    background_color: 'primary',
    is_active: true
  });

  // Button form state
  const [buttonForm, setButtonForm] = useState({
    text: '',
    link: '/',
    icon: 'fas fa-link',
    variant: 'primary'
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchCTAs();
    fetchOptions();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      buttons: [],
      background_color: 'primary',
      is_active: true
    });
    setButtonForm({
      text: '',
      link: '/',
      icon: 'fas fa-link',
      variant: 'primary'
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchCTAs = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/call_to_action.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedCTAs = response.data.data.map(cta => ({
          ...cta,
          id: parseInt(cta.id),
          is_active: cta.is_active === "1" || cta.is_active === 1
        }));
        
        setCtas(processedCTAs);
      } else {
        setError('Failed to load CTAs');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/call_to_action.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setAvailableColors(response.data.data.colors || {});
        setButtonVariants(response.data.data.button_variants || {});
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleButtonFormChange = (e) => {
    const { name, value } = e.target;
    setButtonForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addButton = () => {
    if (!buttonForm.text || !buttonForm.link) {
      setError('Button text and link are required');
      return;
    }
    
    const newButton = { ...buttonForm };
    setFormData(prev => ({
      ...prev,
      buttons: [...prev.buttons, newButton]
    }));
    
    // Reset button form
    setButtonForm({
      text: '',
      link: '/',
      icon: 'fas fa-link',
      variant: 'primary'
    });
  };

  const removeButton = (index) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index)
    }));
  };

  const moveButtonUp = (index) => {
    if (index === 0) return;
    
    const newButtons = [...formData.buttons];
    [newButtons[index - 1], newButtons[index]] = [newButtons[index], newButtons[index - 1]];
    
    setFormData(prev => ({
      ...prev,
      buttons: newButtons
    }));
  };

  const moveButtonDown = (index) => {
    if (index === formData.buttons.length - 1) return;
    
    const newButtons = [...formData.buttons];
    [newButtons[index], newButtons[index + 1]] = [newButtons[index + 1], newButtons[index]];
    
    setFormData(prev => ({
      ...prev,
      buttons: newButtons
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (editingCta) {
        response = await axios.put(
          `${API_BASE_URL}/call_to_action.php?id=${editingCta.id}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/call_to_action.php`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingCta ? 'CTA updated!' : 'CTA added!');
        setShowModal(false);
        resetForm();
        setEditingCta(null);
        fetchCTAs();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving CTA: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this CTA?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/call_to_action.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('CTA deleted!');
          fetchCTAs();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete CTA: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting CTA');
        console.error(err);
      }
    }
  };

  const handleEdit = (cta) => {
    setEditingCta(cta);
    setFormData({
      title: cta.title,
      description: cta.description || '',
      buttons: cta.buttons || [],
      background_color: cta.background_color || 'primary',
      is_active: cta.is_active === true || cta.is_active === "1" || cta.is_active === 1
    });
    
    setShowModal(true);
  };

  const handleToggleActive = async (cta) => {
    try {
      const currentActive = cta.is_active === true || cta.is_active === "1" || cta.is_active === 1;
      const newActiveStatus = !currentActive;
      
      // Deactivate all other CTAs if activating this one
      if (newActiveStatus) {
        const token = localStorage.getItem('token');
        // First deactivate all
        for (const item of ctas) {
          await axios.put(
            `${API_BASE_URL}/call_to_action.php?id=${item.id}`,
            {
              title: item.title,
              description: item.description || '',
              buttons: item.buttons || [],
              background_color: item.background_color || 'primary',
              is_active: 0
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
        }
      }
      
      const updatedData = {
        title: cta.title,
        description: cta.description || '',
        buttons: cta.buttons || [],
        background_color: cta.background_color || 'primary',
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/call_to_action.php?id=${cta.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchCTAs();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCta(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No description';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getColorClass = (color) => {
    switch(color) {
      case 'primary': return 'bg-primary';
      case 'secondary': return 'bg-secondary';
      case 'success': return 'bg-success';
      case 'danger': return 'bg-danger';
      case 'warning': return 'bg-warning';
      case 'info': return 'bg-info';
      case 'dark': return 'bg-dark';
      case 'light': return 'bg-light text-dark';
      default: return 'bg-primary';
    }
  };

  const getVariantClass = (variant) => {
    if (variant.startsWith('outline-')) {
      return `btn-outline-${variant.replace('outline-', '')}`;
    }
    return `btn-${variant}`;
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading CTAs...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Call to Action Manager</h2>
            <p className="text-muted">Manage your website call to action sections</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add CTA
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Buttons</th>
                  <th>Background</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ctas.map((cta) => (
                  <tr key={cta.id}>
                    <td>
                      <strong>{cta.title}</strong>
                    </td>
                    <td>
                      <small className="text-muted">
                        {truncateText(cta.description, 60)}
                      </small>
                    </td>
                    <td>
                      {cta.buttons && cta.buttons.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {cta.buttons.map((btn, index) => (
                            <Badge key={index} bg="secondary" className="me-1">
                              {btn.text}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No buttons</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className={`rounded-circle me-2 ${getColorClass(cta.background_color)}`}
                          style={{ width: '20px', height: '20px' }}
                          title={availableColors[cta.background_color] || cta.background_color}
                        ></div>
                        <small>{availableColors[cta.background_color] || cta.background_color}</small>
                      </div>
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={cta.is_active === true || cta.is_active === "1" || cta.is_active === 1}
                        onChange={() => handleToggleActive(cta)}
                        label={(cta.is_active === true || cta.is_active === "1" || cta.is_active === 1) ? 'Active' : 'Inactive'}
                        key={`active-${cta.id}-${cta.is_active}`}
                      />
                      {cta.is_active && (
                        <small className="text-success d-block">
                          <i className="fas fa-check-circle me-1"></i>
                          Currently displayed
                        </small>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDate(cta.created_at)}
                      </small>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handleEdit(cta)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(cta.id)}
                        disabled={cta.is_active}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {ctas.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-bullhorn fa-3x mb-3"></i>
                <p>No CTAs found. Add your first call to action!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit CTA Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCta ? 'Edit Call to Action' : 'Add Call to Action'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Start Your Journey Today"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={formData.description}
                      onChange={handleTextareaChange}
                      rows={3}
                      placeholder="Enter a compelling description..."
                    />
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Background Color</Form.Label>
                    {optionsLoading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <Form.Select
                        name="background_color"
                        value={formData.background_color}
                        onChange={handleInputChange}
                      >
                        {Object.entries(availableColors).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active (Will be displayed on website)"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Only one CTA can be active at a time
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Buttons Management */}
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Action Buttons</h6>
                  <small className="text-muted">Add buttons that users can click on</small>
                </Card.Header>
                <Card.Body>
                  {/* Add Button Form */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Button Text *</Form.Label>
                        <Form.Control
                          type="text"
                          name="text"
                          value={buttonForm.text}
                          onChange={handleButtonFormChange}
                          placeholder="e.g., Get Started"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Link *</Form.Label>
                        <Form.Control
                          type="text"
                          name="link"
                          value={buttonForm.link}
                          onChange={handleButtonFormChange}
                          placeholder="/get-started"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Icon</Form.Label>
                        <Form.Control
                          type="text"
                          name="icon"
                          value={buttonForm.icon}
                          onChange={handleButtonFormChange}
                          placeholder="fas fa-arrow-right"
                        />
                        <Form.Text className="text-muted">
                          FontAwesome icon class
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Variant</Form.Label>
                        {optionsLoading ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <Form.Select
                            name="variant"
                            value={buttonForm.variant}
                            onChange={handleButtonFormChange}
                          >
                            {Object.entries(buttonVariants).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button variant="primary" onClick={addButton}>
                        <i className="fas fa-plus"></i>
                      </Button>
                    </Col>
                  </Row>
                  
                  {/* Buttons List */}
                  {formData.buttons.length > 0 ? (
                    <Table responsive size="sm">
                      <thead>
                        <tr>
                          <th width="50">Order</th>
                          <th>Text</th>
                          <th>Link</th>
                          <th>Icon</th>
                          <th>Variant</th>
                          <th width="150">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.buttons.map((btn, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => moveButtonUp(index)}
                                  disabled={index === 0}
                                  className="me-1"
                                >
                                  <i className="fas fa-arrow-up"></i>
                                </Button>
                                <span className="mx-2">{index + 1}</span>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => moveButtonDown(index)}
                                  disabled={index === formData.buttons.length - 1}
                                >
                                  <i className="fas fa-arrow-down"></i>
                                </Button>
                              </div>
                            </td>
                            <td>{btn.text}</td>
                            <td>
                              <small className="text-muted">{btn.link}</small>
                            </td>
                            <td>
                              {btn.icon && (
                                <i className={btn.icon}></i>
                              )}
                            </td>
                            <td>
                              <Badge bg={btn.variant.replace('outline-', '')}>
                                {buttonVariants[btn.variant] || btn.variant}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removeButton(index)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center py-3 text-muted">
                      <i className="fas fa-mouse-pointer fa-2x mb-2"></i>
                      <p>No buttons added yet. Add your first button!</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
              
              {/* Preview Section */}
              <Card className="border-secondary">
                <Card.Header>
                  <h6 className="mb-0">Preview</h6>
                </Card.Header>
                <Card.Body className={`${getColorClass(formData.background_color)} text-white rounded`}>
                  <div className="p-4 text-center">
                    <h3 className="mb-3">{formData.title || 'Your Title Here'}</h3>
                    {formData.description && (
                      <p className="mb-4">{formData.description}</p>
                    )}
                    
                    {formData.buttons.length > 0 ? (
                      <div className="d-flex flex-wrap justify-content-center gap-3">
                        {formData.buttons.map((btn, index) => (
                          <Button
                            key={index}
                            variant={btn.variant}
                            href={btn.link}
                            className="d-flex align-items-center"
                          >
                            {btn.icon && (
                              <i className={`${btn.icon} me-2`}></i>
                            )}
                            {btn.text || 'Button'}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No buttons to display</p>
                    )}
                  </div>
                </Card.Body>
              </Card>
              
              {editingCta && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created: {formatDate(editingCta.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingCta ? 'Update' : 'Add'} CTA
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default CallToActionManager;